import { withInRange, getPoint, drawSymbol, getElement } from '../../common/utils/helper';
import { Size, PathOption, Rect } from '@syncfusion/ej2-svg-base';
import { markerAnimate, appendChildElement, animateRedrawElement } from '../../common/utils/helper';
import { pointRender } from '../../common/model/constants';
/**
 * The `BubbleSeries` module is used to render the bubble series.
 */
export class BubbleSeries {
    /**
     * Render the Bubble series.
     *
     * @param {Series} series - The series to be rendered.
     * @param {Axis} xAxis - The X-axis associated with the series.
     * @param {Axis} yAxis - The Y-axis associated with the series.
     * @param {boolean} isInverted - Indicates whether the chart is inverted or not.
     * @returns {void}
     * @private
     */
    render(series, xAxis, yAxis, isInverted) {
        const visiblePoints = series.points;
        let radius;
        const value = Math.max(series.chart.initialClipRect.height, series.chart.initialClipRect.width);
        const percentChange = value / 100;
        let maxRadius = series.maxRadius * percentChange;
        let minRadius = series.minRadius * percentChange;
        let maximumSize = null;
        let maxValue = null;
        if ((series.maxRadius === null || series.minRadius === null)) {
            for (const value of series.chart.visibleSeries) {
                if (value.type === 'Bubble' && value.visible === true && (value.maxRadius === null || value.minRadius === null)) {
                    maximumSize = value.sizeMax > maximumSize ? value.sizeMax : maximumSize;
                }
            }
            maxValue = (value / 5) / 2;
            minRadius = maxRadius = 1;
            radius = maxValue * maxRadius;
        }
        else {
            maximumSize = series.sizeMax;
            radius = maxRadius - minRadius;
        }
        for (const bubblePoint of visiblePoints) {
            this.renderPoint(series, bubblePoint, isInverted, radius, maximumSize, minRadius, visiblePoints);
        }
    }
    renderPoint(series, bubblePoint, isInverted, radius, maximumSize, minRadius, visiblePoints, pointUpdate) {
        let startLocation = series.chart.redraw && bubblePoint.symbolLocations ? bubblePoint.symbolLocations[0] : null;
        bubblePoint.symbolLocations = [];
        bubblePoint.regions = [];
        let segmentRadius;
        if (bubblePoint.visible &&
            withInRange(visiblePoints[bubblePoint.index - 1], bubblePoint, visiblePoints[bubblePoint.index + 1], series)) {
            if ((series.maxRadius === null || series.minRadius === null)) {
                segmentRadius = radius * Math.abs(+bubblePoint.size / maximumSize);
            }
            else {
                segmentRadius = minRadius + radius * Math.abs(+bubblePoint.size / maximumSize);
            }
            segmentRadius = segmentRadius || minRadius;
            const argsData = {
                cancel: false, name: pointRender, series: series, point: bubblePoint,
                fill: series.setPointColor(bubblePoint, series.interior),
                border: series.setBorderColor(bubblePoint, { width: series.border.width, color: series.border.color }),
                height: 2 * segmentRadius, width: 2 * segmentRadius
            };
            series.chart.trigger(pointRender, argsData);
            if (!argsData.cancel) {
                bubblePoint.symbolLocations.push(getPoint(bubblePoint.xValue, bubblePoint.yValue, series.xAxis, series.yAxis, isInverted));
                bubblePoint.color = argsData.fill;
                const shapeOption = new PathOption(series.chart.element.id + '_Series_' + series.index + '_Point_' + bubblePoint.index, argsData.fill, argsData.border.width, argsData.border.color, series.opacity, series.border.dashArray);
                if (pointUpdate && getElement(shapeOption.id)) {
                    const markerElement = getElement(shapeOption.id);
                    startLocation = {
                        x: +markerElement.getAttribute('cx'), y: +markerElement.getAttribute('cy')
                    };
                }
                const element = drawSymbol(bubblePoint.symbolLocations[0], 'Circle', new Size(argsData.width, argsData.height), series.marker.imageUrl, shapeOption, bubblePoint.x.toString() + ':' + bubblePoint.yValue.toString(), series.chart.svgRenderer, series.clipRect);
                appendChildElement(series.chart.enableCanvas, series.seriesElement, element, series.chart.redraw, true, 'cx', 'cy', startLocation, null, false, false, null, series.chart.duration, true);
                bubblePoint.regions.push(new Rect(bubblePoint.symbolLocations[0].x - segmentRadius, bubblePoint.symbolLocations[0].y - segmentRadius, 2 * segmentRadius, 2 * segmentRadius));
                bubblePoint.marker = {
                    border: argsData.border, fill: argsData.fill,
                    height: argsData.height, visible: true,
                    shape: 'Circle', width: argsData.width
                };
                if (series.chart.enableCanvas) {
                    series.chart.markerRender.render(series);
                }
                startLocation = series.chart.redraw && !startLocation ? bubblePoint.symbolLocations[0] : startLocation;
                if (series.chart.redraw) {
                    animateRedrawElement(element, series.chart.duration ? series.chart.duration : 300, startLocation, bubblePoint.symbolLocations[0], 'cx', 'cy');
                }
            }
            else {
                bubblePoint.marker = { visible: false };
            }
        }
    }
    updateDirection(series, point, isInverted) {
        const visiblePoints = series.points;
        let radius;
        const value = Math.max(series.chart.initialClipRect.height, series.chart.initialClipRect.width);
        const percentChange = value / 100;
        let maxRadius = series.maxRadius * percentChange;
        let minRadius = series.minRadius * percentChange;
        let maximumSize = null;
        let maxValue = null;
        if ((series.maxRadius === null || series.minRadius === null)) {
            for (const value of series.chart.visibleSeries) {
                if (value.type === 'Bubble' && value.visible === true && (value.maxRadius === null || value.minRadius === null)) {
                    maximumSize = value.sizeMax > maximumSize ? value.sizeMax : maximumSize;
                }
            }
            maxValue = (value / 5) / 2;
            minRadius = maxRadius = 1;
            radius = maxValue * maxRadius;
        }
        else {
            maximumSize = series.sizeMax;
            radius = maxRadius - minRadius;
        }
        for (let i = 0; i < point.length; i++) {
            this.renderPoint(series, series.points[point[i]], isInverted, radius, maximumSize, minRadius, visiblePoints, true);
            if (series.marker.dataLabel.visible && series.chart.dataLabelModule) {
                series.chart.dataLabelModule.commonId = series.chart.element.id + '_Series_' + series.index + '_Point_';
                series.chart.dataLabelModule.renderDataLabel(series, series.points[point[i]], null, series.marker.dataLabel);
            }
        }
    }
    /**
     * To destroy the Bubble.
     *
     * @returns {void}
     * @private
     */
    destroy() {
        /**
         * Destroy method calling here.
         */
    }
    /**
     * Get module name.
     *
     * @returns {string} - Returns the module name.
     */
    getModuleName() {
        /**
         * Returns the module name of the series.
         */
        return 'BubbleSeries';
    }
    /**
     * Animates the series.
     *
     * @param  {Series} series - Defines the series to animate.
     * @returns {void}
     * @private
     */
    doAnimation(series) {
        const duration = series.animation.duration;
        const delay = series.animation.delay;
        const rectElements = series.seriesElement.childNodes;
        let count = 1;
        for (const bubblePoint of series.points) {
            if (!bubblePoint.symbolLocations.length) {
                continue;
            }
            markerAnimate(rectElements[count], delay, duration, series, bubblePoint.index, bubblePoint.symbolLocations[0], false);
            count++;
        }
    }
}
